import { Box, Cover } from '@optiaxiom/react';
import { createColumnHelper } from '@tanstack/react-table';

import { SpecializedAgentExecution } from '@frontend/types/specialized-agent';

import {
  ActionsCell,
  EvaluationScoreCell,
  ExecutionTimeCell,
  ParametersCell,
  StatusCell,
  TimeStampCell,
} from './cells';

const columnHelper = createColumnHelper<SpecializedAgentExecution>();

type TableMeta = {
  onRowClick?: (execution: SpecializedAgentExecution) => void;
  selectedExecutionId?: string | null;
};

const createColumns = ({ baselineEvaluationScore }: { baselineEvaluationScore?: number }) => [
  columnHelper.accessor('created_at', {
    id: 'timestamp',
    header: 'Timestamp',
    size: 190,
    cell: ({ row, table }) => {
      const meta = table.options.meta as TableMeta;
      const status = row.original.status.toLowerCase();
      return (
        <Cover asChild>
          <Box
            onClick={() => meta?.onRowClick?.(row.original)}
            cursor="pointer"
            data-status={status}
          >
            <TimeStampCell timestamp={row.original.created_at} />
          </Box>
        </Cover>
      );
    },
  }),
  columnHelper.display({
    id: 'parameters',
    header: 'Parameters',
    size: 550,
    cell: ({ row, table }) => {
      const meta = table.options.meta as TableMeta;
      return (
        <Cover asChild>
          <Box onClick={() => meta?.onRowClick?.(row.original)} cursor="pointer">
            <ParametersCell parameters={row.original.parameters} />
          </Box>
        </Cover>
      );
    },
  }),
  columnHelper.display({
    id: 'evaluation_score',
    header: 'Evaluation Score',
    cell: ({ row, table }) => {
      const meta = table.options.meta as TableMeta;
      return (
        <Cover asChild>
          <Box onClick={() => meta?.onRowClick?.(row.original)} cursor="pointer">
            <EvaluationScoreCell
              evaluationScore={row.original.evaluation_score}
              baselineEvaluationScore={baselineEvaluationScore}
            />
          </Box>
        </Cover>
      );
    },
  }),
  columnHelper.display({
    id: 'execution_time',
    header: 'Execution Time',
    cell: ({ row, table }) => {
      const meta = table.options.meta as TableMeta;
      return (
        <Cover asChild>
          <Box onClick={() => meta?.onRowClick?.(row.original)} cursor="pointer">
            <ExecutionTimeCell time={row.original.execution_time} />
          </Box>
        </Cover>
      );
    },
  }),
  columnHelper.accessor('agent_version', {
    id: 'agent_version',
    header: 'Version',
    size: 80,
    cell: ({ row, table }) => {
      const meta = table.options.meta as TableMeta;
      return (
        <Box onClick={() => meta?.onRowClick?.(row.original)} cursor="pointer" w="full">
          V{row.original.agent_version}
        </Box>
      );
    },
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ row, table }) => {
      const meta = table.options.meta as TableMeta;
      return (
        <Cover asChild>
          <Box onClick={() => meta?.onRowClick?.(row.original)} cursor="pointer">
            <StatusCell status={row.original.status} />
          </Box>
        </Cover>
      );
    },
  }),
  columnHelper.display({
    id: 'actions',
    header: 'Actions',
    size: 80,
    enableResizing: false,
    cell: ({ row }) => {
      return <ActionsCell execution={row.original} />;
    },
  }),
];

export default createColumns;
